/**
 * PageBuilderIndexRenderer - Renders PAGE_BUILDER_INDEX via Page Builder system
 * 
 * Stage A of Recursive Self-Editing:
 * - PAGE_BUILDER_INDEX is rendered by PageBuilderRenderer
 * - Contains admin_pages_list section that renders the page registry
 * - The Page Builder page is itself managed by the Page Builder!
 */
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../App';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Layout, Palette, Plus } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { DesignTokensEditor } from '../../components/page-builder';
import { AdminPagesListSection } from '../../admin/page_builder';
import { Badge } from '../../components/ui/badge';
import NewPageModal from '../../components/page-builder/wysiwyg/NewPageModal';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Section Renderer - Handles ALL template types for admin pages
 * Supports all 27+ templates defined in the system
 */
const SectionRenderer = ({ block }) => {
  const type = block._templateId || block.type;
  const props = block.props || {};
  const layout = block.layout?.preset || 'single';
  const background = block.background || {};
  
  // Build background styles
  let bgStyle = {};
  if (background.type === 'solid' && background.config?.color) {
    bgStyle.backgroundColor = background.config.color;
  } else if (background.type === 'gradient' && background.config) {
    const { direction, from, to } = background.config;
    const dir = direction || 'to-bottom';
    bgStyle.background = `linear-gradient(${dir.replace('to-', 'to ')}, ${from}, ${to})`;
  }
  
  // Generic section wrapper
  const renderSection = (content) => (
    <section 
      className="relative py-8" 
      style={bgStyle}
      data-section-type={type}
    >
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        {content}
      </div>
    </section>
  );
  
  // Route by template type
  switch (type) {
    // Admin sections
    case 'admin_pages_list':
      return <AdminPagesListSection settings={props} />;
    
    // Hero sections
    case 'hero_minimal':
      return renderSection(
        <div className={`py-4 ${props.alignment === 'center' ? 'text-center' : ''}`}>
          <h1 className="text-3xl font-bold text-white mb-2">{props.heading || 'Page Title'}</h1>
          {props.subheading && <p className="text-lg text-zinc-400">{props.subheading}</p>}
          {props.showDivider && <div className="mt-4 border-b border-zinc-800" />}
        </div>
      );
    
    case 'hero_overlay':
      return renderSection(
        <div className="text-center py-12">
          {props.showBadge && props.badge && (
            <Badge className="mb-4 bg-orange-500/20 text-orange-400 border-orange-500/30">
              {props.badge}
            </Badge>
          )}
          <h1 className="text-4xl font-bold text-white mb-4">{props.heading}</h1>
          <p className="text-lg text-zinc-300 max-w-2xl mx-auto mb-6">{props.subheading}</p>
          <div className="flex justify-center gap-4">
            {props.ctaPrimaryLabel && (
              <a href={props.ctaPrimaryUrl || '#'} className="px-6 py-3 bg-orange-500 text-white rounded-lg">
                {props.ctaPrimaryLabel}
              </a>
            )}
            {props.ctaSecondaryLabel && (
              <a href={props.ctaSecondaryUrl || '#'} className="px-6 py-3 border border-zinc-600 text-white rounded-lg">
                {props.ctaSecondaryLabel}
              </a>
            )}
          </div>
        </div>
      );
    
    case 'hero_split':
      return renderSection(
        <div className="grid md:grid-cols-2 gap-8 items-center py-8">
          <div>
            <h1 className="text-3xl font-bold text-white mb-4">{props.heading}</h1>
            <p className="text-zinc-400 mb-6">{props.subheading}</p>
            {props.ctaLabel && (
              <a href={props.ctaUrl || '#'} className="px-6 py-3 bg-orange-500 text-white rounded-lg inline-block">
                {props.ctaLabel}
              </a>
            )}
          </div>
          <div className="bg-zinc-800 rounded-lg aspect-video" />
        </div>
      );
    
    // Content sections
    case 'trending_rail':
    case 'featured_grid':
    case 'new_releases_rail':
    case 'continue_reading':
    case 'genre_spotlight':
    case 'community_picks':
    case 'art_gallery_grid':
      return renderSection(
        <div>
          {props.showHeading !== false && (
            <h2 className="text-xl font-semibold text-white mb-6">{props.heading}</h2>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(Math.min(props.maxItems || 4, 8))].map((_, i) => (
              <div key={i} className="aspect-[3/4] bg-zinc-800 rounded-lg" />
            ))}
          </div>
        </div>
      );
    
    // Creator sections
    case 'creator_spotlight':
    case 'creator_grid':
    case 'leaderboard_strip':
      return renderSection(
        <div>
          {props.showHeading !== false && (
            <h2 className="text-xl font-semibold text-white mb-6">{props.heading}</h2>
          )}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {[...Array(Math.min(props.maxItems || 4, 8))].map((_, i) => (
              <div key={i} className="p-4 bg-zinc-800 rounded-lg text-center">
                <div className="w-12 h-12 bg-zinc-700 rounded-full mx-auto mb-2" />
                <div className="h-3 bg-zinc-700 rounded w-2/3 mx-auto" />
              </div>
            ))}
          </div>
        </div>
      );
    
    case 'creator_cta':
      return renderSection(
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">{props.heading}</h2>
            <p className="text-zinc-400 mb-6">{props.subheading}</p>
            {props.ctaLabel && (
              <a href={props.ctaUrl || '#'} className="px-6 py-3 bg-orange-500 text-white rounded-lg inline-block">
                {props.ctaLabel}
              </a>
            )}
          </div>
          <div className="bg-zinc-800 rounded-lg aspect-video" />
        </div>
      );
    
    // CTA sections
    case 'cta_banner':
      return (
        <section className="py-6 bg-gradient-to-r from-orange-500 to-orange-600">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <h2 className="text-xl font-bold text-white mb-2">{props.heading}</h2>
            <p className="text-white/80 mb-4">{props.subheading}</p>
            {props.ctaLabel && (
              <a href={props.ctaUrl || '#'} className="px-6 py-2 bg-white text-orange-600 rounded-lg inline-block">
                {props.ctaLabel}
              </a>
            )}
          </div>
        </section>
      );
    
    case 'transparency_footer':
      return (
        <section className="py-12 bg-black">
          <div className="max-w-4xl mx-auto px-4 text-center">
            {props.showHeading && <h2 className="text-lg font-semibold text-white mb-6">{props.heading}</h2>}
            <p className="text-zinc-600 text-sm">{props.footerText}</p>
          </div>
        </section>
      );
    
    case 'announcement_banner':
      return (
        <section className="py-3 bg-blue-600 text-white text-center">
          <p className="text-sm">{props.message}</p>
        </section>
      );
    
    case 'category_spotlight':
      return renderSection(
        <div className="grid md:grid-cols-2 gap-8 items-center">
          <div>
            <h2 className="text-2xl font-bold text-white mb-4">{props.heading}</h2>
            <p className="text-zinc-400 mb-6">{props.subheading}</p>
          </div>
          <div className="bg-zinc-800 rounded-lg aspect-video" />
        </div>
      );
    
    // Reader sections (placeholders for Phase 3)
    case 'chapter_list':
    case 'vertical_reader_stack':
    case 'horizontal_reader':
    case 'page_by_page_reader':
      return renderSection(
        <div className="py-8 text-center text-zinc-500">
          Reader section: {type} (Phase 3)
        </div>
      );
    
    // Default: render generic section without warning
    default:
      return renderSection(
        <div className="py-4 text-center">
          <p className="text-zinc-500 text-sm">Section: {type}</p>
        </div>
      );
  }
};

const PageBuilderIndexRenderer = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showDesignTokens, setShowDesignTokens] = useState(false);
  const [showNewPageModal, setShowNewPageModal] = useState(false);

  // Listen for inline "New Page" button events from child components
  useEffect(() => {
    const handler = () => setShowNewPageModal(true);
    window.addEventListener('open-new-page-modal', handler);
    return () => window.removeEventListener('open-new-page-modal', handler);
  }, []);

  // Auth check
  useEffect(() => {
    if (authLoading) return;
    
    const token = localStorage.getItem('token');
    const storedUser = localStorage.getItem('user');
    let isAdminUser = user?.role === 'admin' || user?.is_admin;
    
    if (!isAdminUser && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        isAdminUser = parsedUser?.role === 'admin' || parsedUser?.is_admin;
      } catch (e) {}
    }
    
    if (!token || !isAdminUser) {
      navigate('/');
      return;
    }

    fetchPageData();
  }, [user, authLoading, navigate]);

  const fetchPageData = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/admin/page-builder/pages/page_builder_index?version=draft`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPageData(response.data);
    } catch (error) {
      console.error('Error fetching Page Builder index:', error);
      toast.error('Failed to load Page Builder');
    } finally {
      setLoading(false);
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    );
  }

  if (!pageData) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
        <div className="text-center">
          <p className="text-zinc-400">Failed to load Page Builder</p>
          <Button onClick={() => navigate('/admin')} className="mt-4">
            Back to Admin
          </Button>
        </div>
      </div>
    );
  }

  // Sort blocks by order
  const sortedBlocks = [...(pageData.blocks || [])].sort((a, b) => a.order - b.order);
  const enabledBlocks = sortedBlocks.filter(b => b.enabled !== false);

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="page-builder-index-renderer">
      {/* Header */}
      <div className="border-b border-zinc-800 bg-zinc-900/50">
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-500/20 rounded-lg">
                <Layout className="w-6 h-6 text-orange-500" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-white">{pageData.name}</h1>
                <p className="text-sm text-zinc-500">{pageData.description}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                onClick={() => setShowDesignTokens(true)}
                variant="outline"
                size="sm"
                className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
              >
                <Palette className="w-4 h-4 mr-2" />
                Tokens
              </Button>
              <Button
                onClick={() => setShowNewPageModal(true)}
                size="sm"
                className="bg-orange-500 hover:bg-orange-600 text-white"
                data-testid="new-page-button"
              >
                <Plus className="w-4 h-4 mr-1" />
                New Page
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Render blocks from Page Builder definition */}
      <div className="min-h-[calc(100vh-100px)]">
        {enabledBlocks.map((block) => (
          <SectionRenderer 
            key={block.id} 
            block={block}
          />
        ))}
      </div>

      {/* Design Tokens Editor Modal */}
      <DesignTokensEditor
        open={showDesignTokens}
        onClose={() => setShowDesignTokens(false)}
      />
      
      {/* New Page Modal */}
      <NewPageModal
        open={showNewPageModal}
        onClose={() => setShowNewPageModal(false)}
      />
    </div>
  );
};

export default PageBuilderIndexRenderer;
